import { useState, useEffect, useCallback } from "react";
import { NUSDIcon, NibiruIcon } from "../../../../AssetComponents/Images";
import { PurpleButton } from "../../../../Components/Buttons/PurpleButton";
import { BlackButton } from "../../../../Components/Buttons/BlackButton";
import { WhiteButton } from "../../../../Components/Buttons/WhiteButton";
import { toast } from "react-toastify";
import Modal from "react-responsive-modal";
import { GreenButton } from "../../../../Components/Buttons/GreenButton";

/**
 * Mobile Deposit Form Component
 * Displays a simplified deposit form for mobile devices only
 * @param {Object} props
 * @param {Object} props.vault - Vault data object
 * @param {number} props.tokenBalance - User's token balance
 * @param {number} props.maxDeposit - Maximum deposit amount
 * @param {Function} props.onDepositWithAmount - Deposit handler function that accepts amount
 * @param {boolean} props.isConnected - Whether wallet is connected
 * @param {boolean} props.isSubmitting - Whether deposit is in progress
 */
export const DepositFormMobile = ({ 
  vault, 
  tokenBalance, 
  maxDeposit,
  onDepositWithAmount, 
  isConnected,
  isSubmitting: externalIsSubmitting 
}) => {
  const [amount, setAmount] = useState("100");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUsdcBridgeModal, setShowUsdcBridgeModal] = useState(false);
  const [showNibiBridgeModal, setShowNibiBridgeModal] = useState(false);
  const [hideBridgeOnConnect, setHideBridgeOnConnect] = useState(false);

  // Load "do not show again" preference for bridge modals
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("perp_hide_bridge_on_connect");
      if (stored === "true") {
        setHideBridgeOnConnect(true);
      }
    } catch (error) {
      console.error("Error reading bridge preference from localStorage:", error);
    }
  }, []);

  const handleToggleHideBridgeOnConnect = useCallback(() => {
    setHideBridgeOnConnect((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(
          "perp_hide_bridge_on_connect",
          next ? "true" : "false"
        );
      } catch (error) {
        console.error("Error saving bridge preference to localStorage:", error);
      }
      return next;
    });
  }, []);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    
    // Allow empty input
    if (value === '') {
      setAmount('');
      return;
    }
    
    // Only allow numbers and one decimal point
    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }
    
    // Prevent multiple decimal points
    if ((value.match(/\./g) || []).length > 1) {
      return;
    }
    
    // Validate against max deposit and balance
    if (value && parseFloat(value) > maxDeposit) {
      setAmount(maxDeposit.toString());
      toast.info(`Amount limited to maximum deposit of ${maxDeposit} USDC`);
    } else if (tokenBalance && value && parseFloat(value) > parseFloat(tokenBalance)) {
      setAmount(tokenBalance.toString());
    } else {
      setAmount(value);
    }
  };

  const handleMaxClick = () => {
    setAmount(Math.min(tokenBalance, maxDeposit).toString());
  };

  const getExpectedVaultTokens = () => {
    if (!amount || !vault) return "0.00";
    const sharePrice = vault.rawSharePrice || 1.0;
    const expectedTokens = parseFloat(amount) / sharePrice;
    return expectedTokens.toFixed(6);
  };

  const handleDepositClick = async () => {
    if (!isConnected) {
      toast.error("Please connect your wallet");
      return;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setIsSubmitting(true);
    try {
      if (onDepositWithAmount) {
        await onDepositWithAmount(amount);
        setAmount("100");
      }
    } catch (error) {
      console.error("Deposit error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitting = isSubmitting || externalIsSubmitting;

  return (
    <div className="ce-vault-card md:hidden p-4 mb-6" style={{ fontFamily: 'General Sans' }}>
      <div className="flex justify-between items-center mb-4 gap-2">
        <h2 style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>Deposit Amount</h2>
        <span className="ce-tabular" style={{ fontWeight: 500, fontSize: 13, color: 'var(--ink-3)' }}>
          Balance: {isConnected ? tokenBalance : "0.00"} USDC
        </span>
      </div>

      <div className="mb-3">
        <div
          className="ce-field flex items-center justify-between"
          style={{
            height: 52,
            borderRadius: 14,
            border: 0,
            paddingLeft: 16,
            paddingRight: 16,
            background: 'var(--surface-2)',
            boxShadow: 'var(--inset)',
          }}
        >
          <input
            type="text"
            value={amount}
            onChange={handleAmountChange}
            placeholder="0.00"
            disabled={!isConnected || submitting}
            className="bg-transparent outline-none w-full"
            style={{ fontFamily: 'General Sans', fontWeight: 700, fontSize: '26px', lineHeight: '135%', letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums', color: 'var(--ink)' }}
          />
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleMaxClick}
              disabled={!isConnected || submitting || !tokenBalance}
              style={{ fontFamily: 'General Sans', fontWeight: 600, fontSize: 13, color: 'var(--cat-accent, #5B1DEE)', background: 'transparent' }}
              className="hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              MAX
            </button>
            <div className="ce-pill flex items-center gap-1.5" style={{ padding: '5px 10px' }}>
              <NUSDIcon className="w-5 h-5" />
              <span style={{ fontFamily: 'General Sans', fontWeight: 600, fontSize: 13, color: 'var(--ink-2, #344054)' }}>USDC</span>
            </div>
          </div>
        </div>
      </div>

      <div className="ce-vault-well mb-4" style={{ padding: '2px 14px' }}>
        <div className="ce-vault-rows ce-tabular">
          <div className="flex justify-between items-center gap-3">
            <span style={{ fontWeight: 500, fontSize: 13, color: 'var(--ink-2)' }}>You will receive</span>
            <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--up)' }}>{getExpectedVaultTokens()} SLP-USDC</span>
          </div>
          {vault && (
            <div className="flex justify-between items-center gap-3">
              <span style={{ fontWeight: 500, fontSize: 13, color: 'var(--ink-2)' }}>Exchange rate</span>
              <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--cat-accent, #5B1DEE)' }}>
                1 USDC = {(1 / (vault.rawSharePrice || 1.0)).toFixed(6)} SLP-USDC
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Submit first, bridge helper under it — the same hierarchy the desktop
          ticket takes. */}
      <div className="space-y-3">
        <GreenButton
          className="ce-btn-block"
          text={
            submitting 
              ? "Processing..." 
              : !isConnected 
                ? "Connect Wallet" 
                : "Deposit USDC"
          }
          onClick={handleDepositClick}
          disabled={!isConnected || submitting || !amount || parseFloat(amount) <= 0}
        />

        <WhiteButton
          className="ce-btn-block"
          text={
            <div className="flex items-center justify-center gap-2">
              Get USDC on Nibiru
              <NibiruIcon />
            </div>
          }
          onClick={() => {
            setShowUsdcBridgeModal(true);
          }}
        />
      </div>

      {/* Bridge helper modals */}
      <Modal
        open={showUsdcBridgeModal}
        onClose={() => {
          setShowUsdcBridgeModal(false);
        }}
        center
        closeOnOverlayClick={true}
        closeOnEsc={true}
        classNames={{
          modal:
            "relative max-w-[420px] w-[95vw] md:max-w-[420px] md:w-[95vw] p-4 md:p-6 mx-auto max-h-[90vh] overflow-y-auto",
        }}
      >
        <div className="space-y-2 md:space-y-4">
          <h2 className="text-base md:text-xl font-semibold flex items-center gap-1.5 md:gap-2" style={{ color: "var(--ink)" }}>
            <NibiruIcon className="w-4 h-4 md:w-5 md:h-5" />
            <span>Get USDC on Nibiru</span>
          </h2>
          <p className="text-xs md:text-sm leading-tight md:leading-normal" style={{ color: "var(--ink-2)" }}>
            Follow these steps to bridge USDC to Nibiru using the official Nibiru Bridge.
          </p>
          <ol className="space-y-1.5 md:space-y-3 text-xs md:text-sm list-decimal list-inside leading-relaxed" style={{ color: "var(--ink-2)" }}>
            <li>
              Click <span className="font-semibold">"Open Nibiru Bridge"</span> below. This opens the Nibiru bridge in a new tab.
            </li>
            <li>
              In the bridge, set <span className="font-semibold">Supported Asset</span> to <span className="font-semibold">USD Coin (USDC.e)</span>.
            </li>
            <li>
              Choose your <span className="font-semibold">Source Chain</span> (where your USDC is) and set the <span className="font-semibold">Destination Chain</span> to <span className="font-semibold">Nibiru</span>.
            </li>
            <li>
              Enter the <span className="font-semibold">amount</span> of USDC you want to send and your <span className="font-semibold">Nibiru receiving address</span> (your wallet address).
            </li>
            <li>
              Click <span className="font-semibold">"Bridge"</span>, confirm the transaction in your wallet, and wait 5–10 minutes for funds to arrive on Nibiru.
            </li>
            <li>
              Once USDC arrives on Nibiru, you’re ready to trade on <span className="font-semibold">CodedEState</span>.
            </li>
          </ol>
          <div className="ce-vault-well p-3 text-[11px] md:text-xs" style={{ color: "var(--ink-2)" }}>
            <p className="font-semibold mb-0.5 md:mb-1">Tip:</p>
            <p className="leading-tight">
              Make sure you have a small amount of gas on your source chain to pay for the bridge transaction.
              Nibiru Bridge also provides free NIBI tokens for gas, so you’re covered once your funds arrive.
            </p>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 pt-0.5 md:pt-1">
            <input
              id="bridge-hide-on-connect-usdc-mobile"
              type="checkbox"
              checked={hideBridgeOnConnect}
              onChange={handleToggleHideBridgeOnConnect}
              className="h-3.5 w-3.5 md:h-4 md:w-4 rounded flex-shrink-0"
              style={{ accentColor: "var(--cat-accent, #5B1DEE)" }}
            />
            <label
              htmlFor="bridge-hide-on-connect-usdc-mobile"
              className="text-[10px] md:text-xs cursor-pointer leading-tight" style={{ color: "var(--ink-3)" }}
            >
              Do not show this again when I connect my wallet
            </label>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-1 md:pt-2">
            <PurpleButton
              text="Open Nibiru Bridge"
              className="w-full sm:w-auto text-xs md:text-sm px-3 md:px-[14px] py-2 md:py-[8px]"
              onClick={() => {
                window.open("https://app.nibiru.fi/bridge", "_blank", "noopener,noreferrer");
              }}
            />
            {/* <WhiteButton
              text="Next: Get NIBI Gas"
              className="w-full sm:w-auto text-xs md:text-sm font-medium text-gray-700 px-3 md:px-[14px] py-2 md:py-[8px]"
              onClick={() => {
                setShowUsdcBridgeModal(false);
                setShowNibiBridgeModal(true);
              }}
            /> */}
          </div>
        </div>
      </Modal>

      {/* <Modal
        open={showNibiBridgeModal}
        onClose={() => setShowNibiBridgeModal(false)}
        center
        closeOnOverlayClick={true}
        closeOnEsc={true}
        classNames={{
          modal:
            "relative max-w-[420px] w-[95vw] md:max-w-[420px] md:w-[95vw] p-4 md:p-6 mx-auto max-h-[90vh] overflow-y-auto",
        }}
      >
        <div className="space-y-2 md:space-y-4">
          <h2 className="text-base md:text-xl font-semibold flex items-center gap-1.5 md:gap-2" style={{ color: "var(--ink)" }}>
            <NibiruIcon className="w-4 h-4 md:w-5 md:h-5" />
            <span>Get NIBI Gas on Nibiru</span>
          </h2>
          <p className="text-xs md:text-sm leading-tight md:leading-normal" style={{ color: "var(--ink-2)" }}>
            You need NIBI tokens to pay for gas fees when trading. Here are two easy ways to get NIBI:
          </p>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 md:p-3 mb-2 md:mb-4">
            <p className="text-xs md:text-sm font-semibold text-green-800 mb-1 md:mb-2">Easiest Option:</p>
            <p className="text-[10px] md:text-xs text-green-700 leading-tight">
              When bridging USDC through Stargate, you can automatically add up to 40 NIBI tokens for gas. Just click "Add more gas" if you see the warning message. This way you get both USDC and NIBI in one transaction!
            </p>
          </div>

          <p className="text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">Or bridge NIBI separately:</p>
          <ol className="space-y-1 md:space-y-2 text-xs md:text-sm text-gray-700 list-decimal list-inside leading-relaxed">
            <li>
              Click <span className="font-semibold">"Open Stargate Bridge"</span> below.
            </li>
            <li>
              <span className="font-semibold">Select your source chain</span> - Choose where you have NIBI tokens.
            </li>
            <li>
              <span className="font-semibold">Select NIBI</span> as the token to bridge.
            </li>
            <li>
              <span className="font-semibold">Set Nibiru</span> as the destination and enter a small amount (0.1-1 NIBI is enough for many transactions).
            </li>
            <li>
              Review and confirm the transaction. Once it completes, you'll have NIBI for gas fees.
            </li>
          </ol>
          <div className="flex items-center gap-1.5 md:gap-2 pt-0.5 md:pt-1">
            <input
              id="bridge-hide-on-connect-nibi-mobile"
              type="checkbox"
              checked={hideBridgeOnConnect}
              onChange={handleToggleHideBridgeOnConnect}
              className="h-3.5 w-3.5 md:h-4 md:w-4 rounded flex-shrink-0"
              style={{ accentColor: "var(--cat-accent, #5B1DEE)" }}
            />
            <label
              htmlFor="bridge-hide-on-connect-nibi-mobile"
              className="text-[10px] md:text-xs text-gray-600 cursor-pointer leading-tight"
            >
              Do not show this again when I connect my wallet
            </label>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-1 md:pt-2">
            <BlackButton
              text="Open Stargate Bridge"
              className="w-full sm:w-auto text-xs md:text-sm px-3 md:px-[14px] py-2 md:py-[8px]"
              onClick={() => {
                window.open("https://stargate.finance/?dstChain=nibiru&dstToken=0x0829F361A05D993d5CEb035cA6DF3446b060970b", "_blank", "noopener,noreferrer");
              }}
            />
            <WhiteButton
              text="Close"
              className="w-full sm:w-auto text-xs md:text-sm font-medium text-gray-700 px-3 md:px-[14px] py-2 md:py-[8px]"
              onClick={() => setShowNibiBridgeModal(false)}
            />
          </div>
        </div>
      </Modal> */}
    </div>
  );
};

